import React, { useState } from "react";

import { Icon } from "@iconify/react";
import {
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  RefreshCwIcon,
} from "lucide-react";
import { Button } from "js/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";
import { toast } from "utils/toast";

interface ShareModalProps {
  open: boolean;
  onCancel: () => void;
  reportLink: string;
  tenantPhoneNumber?: string;
  landlordPhoneNumber?: string;
  guarantorPhoneNumber?: string;
  onRegenerateLink: () => void;
  isRegenerating: boolean;
}

const truncateUrl = (url: string, maxLength: number = 60): string => {
  if (url.length <= maxLength) return url;

  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname;
    const path = urlObj.pathname + urlObj.search;

    const domainPart = `${urlObj.protocol}//${domain}`;
    const remainingLength = maxLength - domainPart.length - 3;

    if (remainingLength > 10) {
      const pathEnd = path.slice(-remainingLength);
      return `${domainPart}...${pathEnd}`;
    } else {
      return url.slice(0, maxLength - 3) + "...";
    }
  } catch {
    return url.slice(0, maxLength - 3) + "...";
  }
};

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onCancel,
  reportLink,
  tenantPhoneNumber,
  landlordPhoneNumber,
  onRegenerateLink,
  isRegenerating,
}) => {
  const [showFullUrl, setShowFullUrl] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(reportLink);
      toast.success("Enlace copiado al portapapeles");
    } catch (err: any) {
      toast.error(
        `No se pudo copiar el enlace: ${err?.message ?? "error desconocido"}`,
      );
    }
  };

  const phoneNumbers = [
    { label: "Inquilino", number: tenantPhoneNumber },
    { label: "Propietario", number: landlordPhoneNumber },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Compartir Multireporte Inquilino</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Instructions */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">
              Instrucciones para compartir:
            </h4>
            <ol className="list-decimal pl-5 text-sm text-muted-foreground space-y-1">
              <li>Copia el enlace del Multireporte</li>
              <li>
                Compártelo con tus clientes (inquilino y/o propietario) a través
                del medio de tu preferencia
              </li>
              <li>
                El cliente podrá acceder al reporte usando el correo registrado
                en la plataforma
              </li>
            </ol>
            <p className="text-sm text-muted-foreground">
              <strong>NOTA</strong>: Recuerda que el enlace expira en 15 días.
              Si el cliente no puede acceder al reporte, puedes generar un nuevo
              enlace.
            </p>
          </div>

          {/* WhatsApp Share */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Compartir por WhatsApp:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {phoneNumbers.map(
                ({ label, number }) =>
                  number && (
                    <Button
                      key={label}
                      variant="outline"
                      className="border-green-300 text-green-700 hover:bg-green-50"
                      onClick={() => {
                        const whatsappMessage = `Hola,\nTe comparto el resultado del Multireporte Inquilino. Puedes consultarlo en el siguiente enlace: ${reportLink}`;
                        window.open(
                          `https://wa.me/${number}?text=${encodeURIComponent(whatsappMessage)}`,
                          "_blank",
                        );
                      }}
                    >
                      <Icon icon="ic:baseline-whatsapp" fontSize={20} />
                      {label}
                    </Button>
                  ),
              )}
            </div>
          </div>

          {/* Link Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Enlace del reporte:</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFullUrl(!showFullUrl)}
              >
                {showFullUrl ? (
                  <EyeOffIcon className="size-3.5" />
                ) : (
                  <EyeIcon className="size-3.5" />
                )}
                {showFullUrl ? "Ocultar" : "Ver completo"}
              </Button>
            </div>
            <div
              className="rounded-md bg-muted p-3 text-sm font-mono"
              style={{
                wordBreak: showFullUrl ? "break-all" : "break-word",
                fontSize: showFullUrl ? "11px" : "13px",
                maxHeight: showFullUrl ? "none" : "60px",
                overflow: showFullUrl ? "visible" : "hidden",
              }}
            >
              {showFullUrl ? reportLink : truncateUrl(reportLink)}
            </div>
            <div className="flex flex-col gap-2">
              <Button variant="outline" onClick={handleCopyLink} className="w-full">
                <CopyIcon className="size-4" />
                Copiar Enlace
              </Button>
              <Button
                variant="secondary"
                onClick={onRegenerateLink}
                disabled={isRegenerating}
                className="w-full"
              >
                <RefreshCwIcon className={`size-4 ${isRegenerating ? "animate-spin" : ""}`} />
                Regenerar Enlace
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
